import { Component, EventEmitter, HostListener, Input, Output, ViewEncapsulation } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Participant, SessionGroup } from '../../invitations/models/invitation.model';
import { Session } from '../models/session.model';

/**
 * The drag-and-drop board. Deliberately knows nothing about invitations: it takes a session plus
 * the participant list, and emits moves. The panel does the persisting.
 *
 * Also owns cog-menu mutual exclusion: a `SessionGroupColumnComponent` can't see its siblings, so
 * only the board can guarantee that opening one column's menu closes any other, and that a click
 * outside every menu closes whichever is open.
 */
@Component({
  selector: 'app-session-board',
  templateUrl: './session-board.component.html',
  styleUrls: ['./session-board.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SessionBoardComponent {
  @Input() session!: Session;
  @Input() participants: Participant[] = [];
  @Input() selectedIds: string[] = [];
  /** Selected GROUP ids — a separate axis from `selectedIds`, which selects participants. */
  @Input() selectedGroupIds: string[] = [];

  @Output() move = new EventEmitter<{ memberId: string; toGroupId: string; toIndex: number }>();
  @Output() toggleGroup = new EventEmitter<string>();
  @Output() toggleMember = new EventEmitter<string>();
  @Output() renameGroup = new EventEmitter<{ groupId: string; name: string }>();
  @Output() deleteGroup = new EventEmitter<string>();
  @Output() selectGroup = new EventEmitter<string>();
  @Output() downloadGroup = new EventEmitter<string>();

  /** The one column whose cog menu is open, or null. Mutual exclusion lives here. */
  openMenuGroupId: string | null = null;

  /** Every list id, so CDK permits transfer between any two columns. */
  get listIds(): string[] { return this.session.groups.map(g => 'invs-list-' + g.id); }

  /**
   * Resolve a group's ids to participants, in group order. Unresolvable ids are dropped rather
   * than rendered blank — removeParticipant prunes the session, so this should never bite, but a
   * stale localStorage payload from an older shape could.
   */
  membersOf(group: SessionGroup): Participant[] {
    return group.memberIds
      .map(id => this.participants.find(p => p.id === id))
      .filter((p): p is Participant => !!p);
  }

  trackGroup = (_: number, g: SessionGroup) => g.id;

  onDropped(event: CdkDragDrop<SessionGroup>, target: SessionGroup): void {
    const from = event.previousContainer.data as SessionGroup;
    const memberId = from.memberIds[event.previousIndex];
    if (!memberId) { return; }
    // Emit rather than mutate: the service is the single writer, and it removes-then-inserts, so
    // one path covers both same-group reorder and cross-group transfer.
    this.move.emit({ memberId, toGroupId: target.id, toIndex: event.currentIndex });
  }

  /** Cog clicked on a column: open it, or close it if it was already the open one. */
  toggleMenu(groupId: string): void {
    this.openMenuGroupId = this.openMenuGroupId === groupId ? null : groupId;
  }

  closeMenu(): void {
    this.openMenuGroupId = null;
  }

  /** Click anywhere outside a cog/menu closes whatever's open. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.openMenuGroupId) { return; }
    const target = event.target as HTMLElement | null;
    if (target?.closest('.invs-col__cog') || target?.closest('.invs-menu')) { return; }
    this.closeMenu();
  }
}
